<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Employees</h1>
            <p class="text-muted mb-0">
              Your team's details, salary and statutory numbers for payroll
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add employee
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Employee Insights -->
    <CRow class="mb-4">
      <CCol lg="3" md="6">
        <CCard class="border-0">
          <CCardBody class="p-4">
            <CCallout color="info" class="mb-0">
              <div class="text-muted small mb-1">Employees</div>
              <div class="font-weight-bold h4 mb-0">
                {{ items.length }}
              </div>
              <div class="small text-muted">
                {{ activeEmployeesCount }} active
              </div>
            </CCallout>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol lg="3" md="6">
        <CCard class="border-0">
          <CCardBody class="p-4">
            <CCallout color="success" class="mb-0">
              <div class="text-muted small mb-1">Active</div>
              <div class="font-weight-bold h4 mb-0">
                {{ activeEmployeesCount }}
              </div>
              <div class="small text-muted">
                {{ activePercent }}% of your team
              </div>
            </CCallout>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol lg="3" md="6">
        <CCard class="border-0">
          <CCardBody class="p-4">
            <CCallout color="warning" class="mb-0">
              <div class="text-muted small mb-1">On leave</div>
              <div class="font-weight-bold h4 mb-0">
                {{ onLeaveCount }}
              </div>
              <div class="small text-muted">
                Temporary absence
              </div>
            </CCallout>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol lg="3" md="6">
        <CCard class="border-0">
          <CCardBody class="p-4">
            <CCallout color="secondary" class="mb-0">
              <div class="text-muted small mb-1">Inactive</div>
              <div class="font-weight-bold h4 mb-0">
                {{ inactiveEmployeesCount }}
              </div>
              <div class="small text-muted">
                Resigned or left
              </div>
            </CCallout>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Employee List -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <!-- Advanced Filters -->
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <CInput
                    label="Name"
                    v-model="filters.fullName"
                    placeholder="Search by name"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    label="Email"
                    v-model="filters.email"
                    placeholder="Search by email"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Role"
                    :value.sync="filters.role"
                    :options="roleOptions"
                  >
                  </CSelect>
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Employment status"
                    :value.sync="filters.employmentStatus"
                    :options="employmentStatusOptions"
                  />
                </CCol>
              </CRow>
              <CRow class="mt-3">
                <CCol class="d-flex justify-content-end">
                  <CButton color="light" @click="resetFilters">
                    <CIcon name="cil-x" /> Clear filters
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <!-- Toolbar -->
        <CRow class="mb-4 align-items-center">
          <CCol xs="12" md="6" class="mb-3 mb-md-0">
            <CButtonGroup>
              <CButton color="light" @click="showFilters = !showFilters">
                <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
                {{ showFilters ? "Hide filters" : "Show filters" }}
              </CButton>
              <CBadge
                v-if="hasActiveFilters"
                color="primary"
                class="ml-2 align-self-center"
              >
                {{ activeFiltersCount }} active
              </CBadge>
            </CButtonGroup>
          </CCol>
          <CCol xs="12" md="6" class="d-flex justify-content-md-end">
            <span v-if="items.length" class="text-muted small">{{ totalItems }} of {{ items.length }} employees</span>
          </CCol>
        </CRow>

        <!-- First load -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="mt-3 text-muted">Loading employees…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError && !items.length" class="text-center py-5">
          <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
          <h2 class="h5">Couldn't load employees</h2>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-people" size="3xl" class="mb-3 text-muted" />
          <h2 class="h5">No employees yet</h2>
          <p class="text-muted mb-3">Add your first employee to run payroll and give them access to their payslips.</p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add employee
          </CButton>
        </div>

        <!-- Filtered empty -->
        <div v-else-if="!computedItems.length" class="text-center py-5">
          <CIcon name="cil-filter" size="3xl" class="mb-3 text-muted" />
          <h2 class="h5">No employees match your filters</h2>
          <CButton color="light" class="mt-2" @click="resetFilters">
            <CIcon name="cil-x" /> Clear filters
          </CButton>
        </div>

        <template v-else>
        <CProgress v-if="loading" :value="100" animated color="primary" size="xs" class="mb-2" />
        <!-- Desktop View -->
        <div class="d-none d-lg-block">
          <CDataTable
            :items="computedItems"
            :fields="fields"
            hover
            sorter
            responsive
            pagination
            :items-per-page="25"
          >
            <!-- Index Column -->
            <template #show_index="{ index }">
              <td class="py-2 text-muted">
                {{ index + 1 }}
              </td>
            </template>

            <!-- Employee Name Column -->
            <template #fullName="{ item }">
              <td class="py-2">
                <router-link :to="{ name: 'EmployeeById', params: { id: item.id } }" class="font-weight-bold">
                  {{ item.fullName }}
                </router-link>
                <small class="text-muted">{{ item.email }}</small>
              </td>
            </template>

            <!-- Role Column -->
            <template #role="{ item }">
              <td class="py-2">
                {{ item.role }}
              </td>
            </template>

            <!-- Basic Salary Column -->
            <template #basicSalary="{ item }">
              <td class="py-2 text-right">
                <div v-if="item.basicSalary">
                  {{ formatCurrency(item.basicSalary) }}
                </div>
                <small v-else class="text-muted">Not set</small>
              </td>
            </template>

            <!-- Join Date Column -->
            <template #displayJoinDate="{ item }">
              <td class="py-2">
                <div v-if="item.joinDate">{{ item.displayJoinDate }}</div>
                <small v-else class="text-muted">Not set</small>
              </td>
            </template>

            <!-- Employment Status Column -->
            <template #employmentStatus="{ item }">
              <td class="py-2">
                <CBadge :color="getEmploymentStatusBadgeColor(item.employmentStatus)">
                  {{ employmentStatusLabel(item.employmentStatus) }}
                </CBadge>
                <div v-if="item.resignDate" class="mt-1">
                  <small class="text-muted">Resigned: {{ formatDate(item.resignDate) }}</small>
                </div>
              </td>
            </template>

            <!-- System Access Column -->
            <template #isEnable="{ item }">
              <td class="py-2 text-center">
                <CBadge :color="item.isEnable ? 'success' : 'secondary'">
                  {{ item.isEnable ? 'Can sign in' : 'No access' }}
                </CBadge>
              </td>
            </template>

            <!-- Actions Column -->
            <template #show_details="{ item }">
              <td class="py-2 position-relative">
                <div class="text-right action-buttons">
                  <CButtonGroup>
                    <CButton
                      color="light"
                      size="sm"
                      @click="onEdit(item)"
                      title="Edit"
                      :aria-label="`Edit ${item.fullName}`"
                      class="border"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CDropdown
                      size="sm"
                      color="light"
                      placement="bottom-end"
                      class="dropdown-with-zindex border"
                    >
                      <template #toggler-content>
                        <CIcon name="cil-options" />
                        <span class="sr-only">More actions</span>
                      </template>
                      <CDropdownItem @click="onEdit(item)">
                        <CIcon name="cil-pencil" class="mr-2" /> Edit employee
                      </CDropdownItem>
                      <CDropdownItem @click="viewPayslips(item)">
                        <CIcon name="cil-file" class="mr-2" /> View payslips
                      </CDropdownItem>
                      <CDropdownItem @click="viewPayroll(item)">
                        <CIcon name="cil-money" class="mr-2" /> Payroll history
                      </CDropdownItem>
                      <CDropdownDivider v-if="item.resignDate" />
                      <CDropdownItem v-if="item.resignDate" @click="rehireEmployee(item)">
                        <CIcon name="cil-reload" class="mr-2" /> Rehire employee
                      </CDropdownItem>
                      <CDropdownDivider />
                      <CDropdownItem
                        @click="showDeleteConfirmation(item)"
                        class="text-danger"
                      >
                        <CIcon name="cil-trash" class="mr-2" /> Delete
                      </CDropdownItem>
                    </CDropdown>
                  </CButtonGroup>
                </div>
              </td>
            </template>

          </CDataTable>
        </div>

        <!-- Mobile/Tablet View -->
        <div class="d-lg-none">
          <!-- Mobile Cards -->
          <CCard
            v-for="item in computedItems"
            :key="item.id"
            class="mb-3 border"
          >
            <CCardHeader
              class="d-flex justify-content-between align-items-center py-3"
            >
              <div class="text-truncate mr-2">
                <router-link :to="{ name: 'EmployeeById', params: { id: item.id } }" class="font-weight-bold">{{ item.fullName }}</router-link>
                <small class="d-block text-muted text-truncate">{{ item.email }}</small>
              </div>
              <CBadge :color="getEmploymentStatusBadgeColor(item.employmentStatus)">
                {{ employmentStatusLabel(item.employmentStatus) }}
              </CBadge>
            </CCardHeader>
            <CCardBody class="py-3">
              <CRow>
                <CCol xs="6">
                  <div class="mb-2">
                    <div class="text-muted small">Role</div>
                    <div>{{ item.role }}</div>
                  </div>
                  <div class="mb-2">
                    <div class="text-muted small">Joined</div>
                    <div v-if="item.joinDate">{{ item.displayJoinDate }}</div>
                    <small v-else class="text-muted">Not set</small>
                  </div>
                </CCol>
                <CCol xs="6">
                  <div class="mb-2">
                    <div class="text-muted small">Basic salary</div>
                    <div v-if="item.basicSalary" class="font-weight-bold">
                      {{ formatCurrency(item.basicSalary) }}
                    </div>
                    <small v-else class="text-muted">Not set</small>
                  </div>
                  <div class="mb-2">
                    <div class="text-muted small">App access</div>
                    <CBadge :color="item.isEnable ? 'success' : 'secondary'">
                      {{ item.isEnable ? 'Can sign in' : 'No access' }}
                    </CBadge>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
            <CCardFooter class="py-2 d-flex justify-content-between">
              <CButton color="light" @click="onEdit(item)">
                <CIcon name="cil-pencil" /> Edit
              </CButton>
              <CDropdown color="light" placement="bottom-end" class="dropdown-mobile">
                <template #toggler-content>
                  <CIcon name="cil-options" /> More
                </template>
                <CDropdownItem @click="viewPayslips(item)">
                  <CIcon name="cil-file" class="mr-2" /> View payslips
                </CDropdownItem>
                <CDropdownItem @click="viewPayroll(item)">
                  <CIcon name="cil-money" class="mr-2" /> Payroll history
                </CDropdownItem>
                <CDropdownDivider v-if="item.resignDate" />
                <CDropdownItem v-if="item.resignDate" @click="rehireEmployee(item)">
                  <CIcon name="cil-reload" class="mr-2" /> Rehire employee
                </CDropdownItem>
                <CDropdownDivider />
                <CDropdownItem
                  @click="showDeleteConfirmation(item)"
                  class="text-danger"
                >
                  <CIcon name="cil-trash" class="mr-2" /> Delete
                </CDropdownItem>
              </CDropdown>
            </CCardFooter>
          </CCard>

        </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete Confirmation Modal -->
    <CModal
      :title="`Delete ${itemToDelete.fullName || 'this employee'}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
      @update:show="onDeleteConfirmation"
    >
      <p class="mb-2">
        {{ itemToDelete.fullName }} ({{ itemToDelete.email }}) will be removed from your employees and
        won't be able to sign in. This can't be undone.
      </p>
      <p class="text-muted small mb-0">
        If they've left, you can set a resign date on their profile instead and keep their payroll history.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep employee
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" /> Delete employee
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ProfileApi from "@/lib/profileApi";
import { formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

export default {
  name: "EmployeeList",
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      roleOptions: [
        { value: "", label: "All roles" },
        { value: "Accountant", label: "Accountant" },
        { value: "Staff", label: "Staff" },
      ],
      employmentStatusOptions: [
        { value: "", label: "All statuses" },
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
        { value: "OnLeave", label: "On leave" },
      ],
      fields: [
        {
          key: "fullName",
          label: "Employee",
          sorter: true,
          filter: true,
          _style: "min-width:200px"
        },
        {
          key: "role",
          label: "Role",
          sorter: true,
          filter: true,
          _style: "width:120px"
        },
        {
          key: "basicSalary",
          label: "Basic salary",
          sorter: true,
          filter: false,
          _style: "text-align: right; width:150px"
        },
        {
          key: "displayJoinDate",
          label: "Joined",
          sorter: true,
          filter: false,
          _style: "width:150px"
        },
        {
          key: "employmentStatus",
          label: "Status",
          sorter: true,
          filter: true,
          _style: "width:140px"
        },
        {
          key: "isEnable",
          label: "App access",
          sorter: true,
          filter: true,
          _style: "width:120px; text-align:center"
        },
        {
          key: "show_details",
          label: "",
          _style: "width:120px",
          sorter: false,
          filter: false,
        },
      ],
      api: new ProfileApi(),
      warningModal: false,
      itemToDelete: {},
      // Filters
      showFilters: false,
      filters: {
        fullName: "",
        email: "",
        role: "",
        employmentStatus: "",
      },
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Staff", "Employees"]);
    this.refreshTable();
  },
  computed: {
    computedItems() {
      let filtered = this.items.map((item) => {
        return {
          ...item,
          role: item.appUser?.role || 'Staff',
          displayJoinDate: this.formatDate(item.joinDate),
          employmentStatus: item.employmentStatus || 'Active',
          isEnable: item.isEnable !== false
        };
      });

      // Apply filters
      if (this.filters.fullName) {
        const search = this.filters.fullName.toLowerCase();
        filtered = filtered.filter(item =>
          item.fullName?.toLowerCase().includes(search)
        );
      }
      if (this.filters.email) {
        const search = this.filters.email.toLowerCase();
        filtered = filtered.filter(item =>
          item.email?.toLowerCase().includes(search)
        );
      }
      if (this.filters.role) {
        filtered = filtered.filter(item => item.role === this.filters.role);
      }
      if (this.filters.employmentStatus) {
        filtered = filtered.filter(item =>
          item.employmentStatus === this.filters.employmentStatus
        );
      }

      return filtered;
    },
    totalItems() {
      return this.computedItems.length;
    },
    // KPI tiles describe the whole team, not the filtered view.
    allStatuses() {
      return this.items.map((item) => item.employmentStatus || "Active");
    },
    activeEmployeesCount() {
      return this.allStatuses.filter((s) => s === "Active").length;
    },
    inactiveEmployeesCount() {
      return this.allStatuses.filter((s) => s === "Inactive").length;
    },
    onLeaveCount() {
      return this.allStatuses.filter((s) => s === "OnLeave").length;
    },
    activePercent() {
      return this.items.length ? Math.round((this.activeEmployeesCount / this.items.length) * 100) : 0;
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(filter => filter !== "");
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter(filter => filter !== "").length;
    },
  },
  methods: {
    formatDate(dt) {
      return dt ? formatDate(dt) : "";
    },
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },
    // Section 3.3 rules: active = success, on leave = warning (someone may need to act), inactive = grey.
    getEmploymentStatusBadgeColor(status) {
      if (status === "Active") return "success";
      if (status === "OnLeave") return "warning";
      return "secondary";
    },
    employmentStatusLabel(status) {
      if (!status || status === "Active") return "Active";
      if (status === "OnLeave") return "On leave";
      if (status === "Inactive") return "Inactive";
      const words = String(status).replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
      return words.charAt(0).toUpperCase() + words.slice(1);
    },
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning" }[color] || "info";
      this.$toast[kind](message);
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;

      self.api
        .getListByCurrentBusiness()
        .then((response) => {
          self.items = response.result || [];
        })
        .catch((err) => {
          console.error("Couldn't load employees", err);
          self.loadError = true;
          if (self.items.length) {
            self.toast("Error", "Couldn't refresh employees. Check your connection and try again.", "danger");
          }
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onEdit(item) {
      var self = this;
      self.$router.push({ name: "EmployeeById", params: { id: item.id } });
    },
    viewPayslips(item) {
      this.$router.push({ path: "/tenants/paySlipList", query: { search: item.fullName } });
    },
    viewPayroll(item) {
      this.$router.push({ path: "/tenants/payrollList", query: { employee: item.fullName } });
    },
    rehireEmployee(item) {
      var self = this;
      self.$router.push({ name: "EmployeeById", params: { id: item.id } });
      self.toast("Info", "To rehire, clear the resign date and set a new join date", "info");
    },
    showDeleteConfirmation(item) {
      var self = this;
      self.itemToDelete = {
        ...item,
        role: item.appUser?.role || 'Staff'
      };
      self.warningModal = true;
    },
    confirmDelete() {
      this.deleting = true;
      this.api
        .deleteProfile(this.itemToDelete.id)
        .then((response) => {
          this.refreshTable();
          this.toast("Success", "Employee deleted", "success");
          this.warningModal = false;
        })
        .catch((error) => {
          console.error("Delete error:", error);
          this.toast("Error", "Couldn't delete the employee. Try again.", "danger");
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    onDeleteConfirmation(status, evt, accept) {
      if (!status) {
        this.itemToDelete = {};
      }
    },
    addNew() {
      this.$router.push({ name: "Employee" });
    },
    resetFilters() {
      this.filters = {
        fullName: "",
        email: "",
        role: "",
        employmentStatus: "",
      };
    },
    applyFilters() {
      // Filters are applied automatically via computed property
      if (window.innerWidth >= 768) {
        this.showFilters = false;
      }
    },
  },
};
</script>

<style scoped>
/* Fix dropdown z-index issues */
.dropdown-with-zindex {
  position: static;
}

.dropdown-with-zindex .dropdown-menu {
  z-index: 1050 !important;
  position: absolute;
  will-change: transform;
}

.dropdown-mobile .dropdown-menu {
  z-index: 1055 !important;
  position: absolute;
  will-change: transform;
}

/* Enhanced action buttons */
.action-buttons,
.action-buttons .btn-group {
  position: relative;
  z-index: auto;
}

/* Lift the open row above its neighbours so the dropdown menu always
   renders on top instead of behind the rows below it. */
.action-buttons:has(.dropdown.show),
.action-buttons .btn-group:has(.dropdown.show) {
  z-index: 1060;
}

/* Badge size */
.badge {
  font-size: 0.75em;
  font-weight: 500;
}
</style>
